import type { Brand, PhoneModel, RepairOption } from '../types';
import { applyServiceOption, repairForSelection } from './serviceOfferSelection';

export const REPAIR_APPOINTMENT_SOURCE = 'repair';
export type RepairWorkflow = 'appointment' | 'quote';

type EncodedRepair = Readonly<{ id: string; optionId?: string }>;

export type AppointmentSelectionResolution =
  | Readonly<{ status: 'EMPTY' }>
  | Readonly<{ status: 'PENDING'; model?: PhoneModel }>
  | Readonly<{ status: 'INVALID'; reason: string }>
  | Readonly<{ status: 'VALID'; model: PhoneModel; repairs: RepairOption[]; taxIncluded: boolean; workflow: RepairWorkflow }>;

export const canonicalAppointmentIdentity = (value: unknown): string => String(value ?? '').trim();

export function findAppointmentModel(brands: Brand[], modelIdentity: unknown): PhoneModel | null {
  const canonicalIdentity = canonicalAppointmentIdentity(modelIdentity);
  if (!canonicalIdentity) return null;
  return brands
    .flatMap(brand => brand.models)
    .find(candidate => canonicalAppointmentIdentity(candidate.id) === canonicalIdentity) || null;
}

function buildRepairWorkflowPath(model: PhoneModel, repairs: RepairOption[], taxIncluded: boolean, workflow: RepairWorkflow): string {
  const modelId = canonicalAppointmentIdentity(model.id);
  const encodedRepairs: EncodedRepair[] = repairs.map(repair => ({
    id: canonicalAppointmentIdentity(repair.id),
    ...(repair.selectedServiceOptionId ? { optionId: canonicalAppointmentIdentity(repair.selectedServiceOptionId) } : {})
  }));
  const parameters = new URLSearchParams({
    appointment: REPAIR_APPOINTMENT_SOURCE,
    workflow,
    model: modelId,
    option: taxIncluded ? 'tax-included' : 'tax-excluded',
    repairs: JSON.stringify(encodedRepairs)
  });
  return `/contact?${parameters.toString()}`;
}

export function buildRepairAppointmentPath(model: PhoneModel, repairs: RepairOption[], taxIncluded: boolean): string {
  return buildRepairWorkflowPath(model, repairs, taxIncluded, 'appointment');
}

export function buildRepairQuotePath(model: PhoneModel, repairs: RepairOption[], taxIncluded: boolean): string {
  return buildRepairWorkflowPath(model, repairs, taxIncluded, 'quote');
}

export function resolveRepairAppointmentSelection(
  brands: Brand[],
  search: string
): AppointmentSelectionResolution {
  const parameters = new URLSearchParams(search);
  if (parameters.get('appointment') !== REPAIR_APPOINTMENT_SOURCE) return { status: 'EMPTY' };
  if (!brands.length) return { status: 'PENDING' };

  const modelId = canonicalAppointmentIdentity(parameters.get('model'));
  const repairsRaw = parameters.get('repairs');
  if (!modelId || !repairsRaw) return { status: 'INVALID', reason: 'APPOINTMENT_SELECTION_IDENTITIES_REQUIRED' };

  const model = findAppointmentModel(brands, modelId);
  if (!model) return { status: 'INVALID', reason: 'APPOINTMENT_MODEL_NOT_FOUND' };
  // A fresh progressive-catalog row can omit repairsLoaded while its service
  // relationships are still being fetched.  Only a positively loaded model
  // may be validated; treating an undefined flag as complete would reject a
  // valid deep link during the first render and permanently clear its service.
  if (model.repairsLoaded !== true) return { status: 'PENDING', model };

  let requested: EncodedRepair[];
  try {
    const parsed: unknown = JSON.parse(repairsRaw);
    if (!Array.isArray(parsed) || parsed.length < 1 || parsed.length > 20) {
      return { status: 'INVALID', reason: 'APPOINTMENT_SERVICE_IDENTITIES_INVALID' };
    }
    requested = parsed.map(value => {
      if (!value || typeof value !== 'object') throw new Error('INVALID');
      const id = canonicalAppointmentIdentity(Reflect.get(value, 'id'));
      const optionId = canonicalAppointmentIdentity(Reflect.get(value, 'optionId'));
      if (!id) throw new Error('INVALID');
      return { id, ...(optionId ? { optionId } : {}) };
    });
  } catch {
    return { status: 'INVALID', reason: 'APPOINTMENT_SERVICE_IDENTITIES_INVALID' };
  }

  if (new Set(requested.map(item => item.id)).size !== requested.length) {
    return { status: 'INVALID', reason: 'APPOINTMENT_SERVICE_IDENTITIES_DUPLICATED' };
  }

  const repairs: RepairOption[] = [];
  for (const identity of requested) {
    const repair = model.repairs.find(candidate => canonicalAppointmentIdentity(candidate.id) === identity.id && candidate.isActive !== false);
    if (!repair) return { status: 'INVALID', reason: 'APPOINTMENT_MODEL_SERVICE_MISMATCH' };
    if (!identity.optionId) {
      repairs.push(repairForSelection(repair));
      continue;
    }
    const option = (repair.serviceOptions || []).find(candidate => canonicalAppointmentIdentity(candidate.id) === identity.optionId);
    if (!option) return { status: 'INVALID', reason: 'APPOINTMENT_SERVICE_OPTION_MISMATCH' };
    repairs.push(applyServiceOption(repair, option));
  }

  return {
    status: 'VALID',
    model,
    repairs,
    taxIncluded: parameters.get('option') === 'tax-included',
    workflow: parameters.get('workflow') === 'quote' ? 'quote' : 'appointment',
  };
}
